import {Badge, CloseButton, Modal, ModalBody, ModalTitle} from "react-bootstrap";
import ModalHeader from "react-bootstrap/ModalHeader";
import React, {useMemo} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faDollar, faEye} from "@fortawesome/free-solid-svg-icons";
import {DIALOG_OPTIONS} from "../../types/DialogOptionsType";
import RRTable from "../../components/rr-table/RRTable";
import CenteredColumn from "../../components/CenteredColumn";
import {dateTimeToString, dateToString, zeroToEmptyStringAsNumber} from "../../tools";
import {ConfirmIconButton} from "../../components/IconButtons";
import {useAppSelector} from "../../hooks";
import {selectDepartment, selectCurrentUser} from "../../../features/AppStateSlice";
import {selectAllEmployees} from "../../../features/AppDataSlice";
import {RREmployeePPH, RREmployeesType, RREmployeeType} from "../../types/RREmployeeType";
import RightAlignedColumn from "../../components/RightAlignedColumn";
import {updateDoc} from "firebase/firestore";
import {canApproveRateChanges} from "../../constants/approveRateChangeUsers";
import {
    approveEmployeeRates,
    getEmployeeRateActor,
    getEmployeeRateEffectiveFrom
} from "../employees/employeeRateAudit";

export function ApprovePPHDialog({zIndex, show, onClose}: DIALOG_OPTIONS) {
    const department = useAppSelector(selectDepartment);
    const currentUser = useAppSelector(selectCurrentUser);
    const allDepEmployees = useAppSelector(selectAllEmployees);
    const canManageRateChanges = canApproveRateChanges(currentUser?.id);

    const employees = useMemo<RREmployeesType>(() => {
        const today = new Date();

        return allDepEmployees.filter(value => {
            if (!value) {
                return false;
            }

            const isInDepartment =
                (department === "montage" && value.isMontage) ||
                (department === "catering" && value.isCatering) ||
                (department === "store" && value.isStore) ||
                (department === "logistics" && value.isLogistics);

            if (!isInDepartment || value.isDeleted || (value.leaveDate && value.leaveDate < today)) {
                return false;
            }

            // Business rule: first ever rate (index 0) is not treated as "rate change".
            return value.empl_pph.some((pph, idx) => idx > 0 && pph.isApproved === false);
        });
    }, [allDepEmployees, department]);

    function getNotApprovedRate(employee: RREmployeeType): RREmployeePPH | undefined {
        for (let i = employee.empl_pph.length - 1; i >= 1; i--) {
            if (!employee.empl_pph[i].isApproved) {
                return employee.empl_pph[i];
            }
        }
    }

    async function approveChange(employee: RREmployeeType) {
        if (!canManageRateChanges) {
            return;
        }

        const newList = approveEmployeeRates(employee.empl_pph, getEmployeeRateActor(currentUser));
        await updateDoc(employee.ref!, {
            empl_pph: newList,
            pph: newList[newList.length - 1]?.pph || 0,
            supervisor_pph: newList[newList.length - 1]?.supervisor_pph || 0
        });
    }

    async function rejectChange(employee: RREmployeeType) {
        if (!canManageRateChanges) {
            return;
        }

        const newList = employee.empl_pph.filter(value => value.isApproved);
        await updateDoc(employee.ref!, {empl_pph: newList});
    }

    return (
        <Modal show={show} size={"xl"} style={{zIndex: zIndex + 1}}>
            <ModalHeader>
                <ModalTitle>
                    <FontAwesomeIcon icon={faDollar}/> Променени ставки
                </ModalTitle>
                <CloseButton onClick={() => onClose(null)}/>
            </ModalHeader>

            <ModalBody>
                <div className={"max-h-50vh scrollable"}>
                    <RRTable tableClassName={"text-nowrap"}>
                        <thead>
                        <tr>
                            <th></th>
                            <th>Име</th>
                            <th className={"text-center"}>Отдел</th>
                            <th className={"text-center"}>Права</th>
                            <RightAlignedColumn header>Базова ставка</RightAlignedColumn>
                            <RightAlignedColumn header>Супервайзор ставка</RightAlignedColumn>
                            <th>Променил</th>
                            <th>Променено на</th>
                            <th>В сила от</th>
                        </tr>
                        </thead>
                        <tbody>
                        <tr>
                            <td></td>
                        </tr>
                        {
                            !canManageRateChanges &&
                            <tr>
                                <CenteredColumn colSpan={9}><strong>Нямате права за одобряване на променени ставки.</strong></CenteredColumn>
                            </tr>
                        }
                        {
                            canManageRateChanges && employees.length > 0
                                ? employees.map(employee => {
                                    const rate = getNotApprovedRate(employee);

                                    return <tr key={employee.id} className={"tr-bordered lh-2em"}>
                                        <td className={"w-75px"}>
                                            <ConfirmIconButton
                                                small
                                                onClick={() => approveChange(employee)}
                                                onReject={() => rejectChange(employee)}
                                                approveTitle={"Одобри променените ставки"}
                                                rejectTitle={"Отхвърли променените ставки"}
                                            />
                                        </td>
                                        <td className={"td-left-dbl-border"}>{employee.name}</td>
                                        <td className={"td-left-dbl-border text-center"}>
                                            <Badge bg={employee.isMontage ? "primary" : "secondary"} title={employee.isMontage ? "Монтаж" : "Не е в отдел монтаж"}>М</Badge>&nbsp;
                                            <Badge bg={employee.isCatering ? "primary" : "secondary"} title={employee.isCatering ? "Кетъринг" : "Не е в отдел кетъринг"}>К</Badge>&nbsp;
                                            <Badge bg={employee.isStore ? "primary" : "secondary"} title={employee.isStore ? "Кухня" : "Не е в отдел кухня"}>С</Badge>
                                        </td>
                                        <td className={"td-left-dbl-border text-center"}>
                                            <Badge bg={employee.isSuperAdmin ? "primary" : "secondary"} title={employee.isSuperAdmin ? "Администратор" : "Не е администратор"}>A</Badge>&nbsp;
                                            <Badge bg={employee.isAdmin ? "primary" : "secondary"} title={employee.isAdmin ? "Оператор" : "Не е оператор"}>О</Badge>&nbsp;
                                            <Badge bg={employee.isEmployee ? "primary" : "secondary"} title={employee.isEmployee ? "Служител" : "Не е служител"}>С</Badge>&nbsp;
                                            <Badge
                                                bg={employee.canSeePayment ? "primary" : "secondary"}
                                                title={`${employee.canSeePayment ? "Може" : "Не може"} да вижда прихода си`}
                                            >
                                                <FontAwesomeIcon icon={faEye}/>
                                            </Badge>
                                        </td>
                                        <td className={"td-left-dbl-border text-end"}>
                                            {zeroToEmptyStringAsNumber(rate?.pph, " €/ч")}
                                        </td>
                                        <td className={"td-left-dbl-border text-end"}>
                                            {zeroToEmptyStringAsNumber(rate?.supervisor_pph, " €/ч")}
                                        </td>
                                        <td className={"td-left-dbl-border"}>{rate?.createdBy?.name || "—"}</td>
                                        <td className={"td-left-dbl-border"}>
                                            {rate?.createdAt ? dateTimeToString(rate.createdAt) : "—"}
                                        </td>
                                        <td className={"td-left-dbl-border"}>
                                            {rate ? dateToString(getEmployeeRateEffectiveFrom(rate)) : "—"}
                                        </td>
                                    </tr>;
                                })
                                : canManageRateChanges &&
                                <tr>
                                    <CenteredColumn colSpan={9}><strong>Няма променени ставки за потвърждаване.</strong></CenteredColumn>
                                </tr>
                        }
                        </tbody>
                    </RRTable>
                </div>
            </ModalBody>
        </Modal>
    );
}
